import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL, APIRequest, TRADING_API_URL } from '../../../app/Api';
import { formatDate, timestampToDate, toFixedWithoutRounding } from '../../../app/Helpers';
import { TableState } from '../../common/Table/TableSate';
import { number } from 'yup';
import { OrderEndpointClient } from '../../../repository/Trading/OrderServiceClientPb';
import { GetOrdersRequest, GetOrdersResponse, CancelResponse, CancelRequest, GetOrderBookRequest, GetOrderBookResponse } from '../../../repository/Trading/order_pb';
import { CurrencyPairEndpointClient } from '../../../repository/Trading/Currency_pairServiceClientPb';
import { GetCurrencyPairsRequest, GetCurrencyPairsResponse } from '../../../repository/Trading/currency_pair_pb';
import { OrderStatus } from '../../../app/Enums';


export interface TradingLayoutState {
    set: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],
    currencyPairs: TableState
    orderBook: TableState


}

const initialState: TradingLayoutState = {
    set: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: [],
    currencyPairs: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    orderBook: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },

}

const client = new OrderEndpointClient(TRADING_API_URL, null, null);
const clientCurrencyPairs = new CurrencyPairEndpointClient(TRADING_API_URL, null, null);


export const getItems = createAsyncThunk<GetOrdersResponse.AsObject, APIRequest<GetOrdersRequest>, {

    rejectValue: ApiMessage
}>(
    'layout/trading/orders/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getOrders(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)

export const getCurrencyPairs = createAsyncThunk<GetCurrencyPairsResponse.AsObject, APIRequest<GetCurrencyPairsRequest>, {

    rejectValue: ApiMessage
}>(
    'layout/trading/orders/currencyPairs/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await clientCurrencyPairs.getCurrencyPairs(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)

export const getOrdersBook = createAsyncThunk<GetOrderBookResponse.AsObject, APIRequest<GetOrderBookRequest>, {

    rejectValue: ApiMessage
}>(
    'layout/trading/ordersBook/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getOrderBook(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)




export const cancelOrder = createAsyncThunk<APIResponse<CancelResponse.AsObject>, APIRequest<CancelRequest>, {

    rejectValue: ApiMessage
}>(
    'layout/trading/orders/cancel',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.cancel(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)
const getFilters = (state: TradingLayoutState): any[] => {


    return [];
}

export const tradingLayoutSlice = createSlice({
    name: 'layout/trading',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.set = initialState.set;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
            state.orderBook = initialState.orderBook;
            state.filters = initialState.filters;
            state.currencyPairs = initialState.currencyPairs;

        },
        resetSet: (state) => {
            state.set.items = [];
            state.set.hasMore = true;
        },
        resetOrderBook: (state) => {
            state.orderBook.items = [];
            state.orderBook.hasMore = true;
        },
        resetCurrencyPairs: (state) => {
            state.currencyPairs.items = [];
            state.currencyPairs.hasMore = true;
        },
        setSort: (state, action: PayloadAction<boolean>) => {
            state.set.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.set.numberOfResults = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<any[]>) => {
            state.set.selected = action.payload;
        },

        setSelectedOrderBookItems: (state, action: PayloadAction<any[]>) => {
            state.orderBook.selected = action.payload;
            state.filters = getFilters(state);

        },

        setSelectedCurrencyPairItems: (state, action: PayloadAction<any[]>) => {
            state.currencyPairs.selected = action.payload;
            state.filters = getFilters(state);

        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.set.isFilteredSet = action.payload;
        },

        setIsFilteredOrderBookSet: (state, action: PayloadAction<boolean>) => {
            state.orderBook.isFilteredSet = action.payload;
        },

        setIsFilteredCurrencyPairsSet: (state, action: PayloadAction<boolean>) => {
            state.currencyPairs.isFilteredSet = action.payload;
        },


        addOrderBookItem: (state, action: PayloadAction<any>) => {
            if (!state.orderBook.isFilteredSet) {
                if (!state.orderBook.hasMore && !state.orderBook.isDescending) {
                    state.orderBook.items.push(action.payload);
                } else if (state.orderBook.isDescending) {
                    state.orderBook.items.unshift(action.payload);
                }
            }
        },
        updateOrderBookItem: (state, action: PayloadAction<any>) => {
            let selected = state.orderBook.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.orderBook.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.orderBook.items[u] = action?.payload
                }
            }

        }

    },
    extraReducers: (builder) => {

        builder.addCase(getOrdersBook.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.entryList.map(val => {
                    return {
                        price: (val.price?.value),
                        tradeAction: val.tradeaction?.value,
                        amount: (val.amount?.value),
                        total: (val.total?.value)
                    }
                }
                ) as any[])

                state.orderBook.items = r
                //console.log(state.items)

            } else {
                state.orderBook.hasMore = false;
            }
            state.orderBook.isFetching = false

        })
        builder.addCase(getOrdersBook.rejected, (state, action) => {
            state.orderBook.isFetching = false;
            if (action.payload) {
                //state.message = action.payload;
            }
        })
        builder.addCase(getOrdersBook.pending, (state, action) => {
            state.orderBook.isFetching = true;
            // state.message = undefined;
        })


        builder.addCase(getCurrencyPairs.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.currencypairsList.map(val => {
                    let rc = {
                        id: val.id,
                        name: val.name?.value,
                        buyPrice: val.buyprice?.value,
                        sellPrice: val.sellprice?.value,
                        tradingPrice: val.tradingprice?.value,
                        isActive: val.isactive?.value,
                        iconSource: val.iconsource?.value ?? "",
                        categoryId: val.category?.id?.value ?? "",
                        categoryNameAr: val.category?.namear?.value ?? "",
                        categoryNameEn: val.category?.nameen?.value ?? "",
                        buyPriceEquation: val.pricetrigger?.buypriceequation?.value ?? "",
                        sellPriceEquation: val.pricetrigger?.sellpriceequation?.value ?? "",
                        tradingPriceEquation: val.pricetrigger?.tradingpriceequation?.value ?? "",
                        actionType: val?.pricetrigger?.actiontype?.value,
                        followPricingRules: val?.pricetrigger?.followpricingrules?.value,
                        syncRate: val?.pricetrigger?.syncrate?.value,

                        isPriceTriggerActive: val.pricetrigger?.isactive?.value,
                        mainCurrencyPair: val.pricetrigger?.main?.id?.value ?? "",
                        mainCurrencyPairName: val.pricetrigger?.main?.name?.value ?? "",
                        mainCurrencyPairBaseCurrencyId: val.pricetrigger?.main?.base?.id?.value ?? "",
                        mainCurrencyPairBaseCurrencyName: val.pricetrigger?.main?.base?.name?.value ?? "",
                        mainCurrencyPairBaseCurrencyCode: val.pricetrigger?.main?.base?.code?.value ?? "",
                        mainCurrencyPairBaseCurrencyDecimalPlaces: val.pricetrigger?.main?.base?.decimalplaces?.value ?? undefined,
                        mainCurrencyPairBaseCurrencyForm: val.pricetrigger?.main?.base?.form?.value ?? undefined,
                        mainCurrencyPairBaseCurrencySymbol: val.pricetrigger?.main?.base?.symbol?.value ?? "",
                        mainCurrencyPairQuoteCurrencyId: val.pricetrigger?.main?.quote?.id?.value ?? "",
                        mainCurrencyPairQuoteCurrencyName: val.pricetrigger?.main?.quote?.name?.value ?? "",
                        mainCurrencyPairQuoteCurrencyCode: val.pricetrigger?.main?.quote?.code?.value ?? "",
                        mainCurrencyPairQuoteCurrencyDecimalPlaces: val.pricetrigger?.main?.quote?.decimalplaces?.value ?? undefined,
                        mainCurrencyPairQuoteCurrencyForm: val.pricetrigger?.main?.quote?.form?.value ?? undefined,
                        mainCurrencyPairQuoteCurrencySymbol: val.pricetrigger?.main?.quote?.symbol?.value ?? "",

                        baseCurrencyId: val.base?.id?.value ?? "",
                        baseCurrencyName: val.base?.name?.value ?? "",
                        baseCurrencyCode: val.base?.code?.value ?? "",
                        baseCurrencyDecimalPlaces: val.base?.decimalplaces?.value ?? undefined,
                        baseCurrencyForm: val.base?.form?.value ?? undefined,
                        baseCurrencySymbol: val.base?.symbol?.value ?? "",
                        quoteCurrencyId: val.quote?.id?.value ?? "",
                        quoteCurrencyName: val.quote?.name?.value ?? "",
                        quoteCurrencyCode: val.quote?.code?.value ?? "",
                        quoteCurrencyDecimalPlaces: val.quote?.decimalplaces?.value ?? undefined,
                        quoteCurrencyForm: val.quote?.form?.value ?? undefined,
                        quoteCurrencySymbol: val.quote?.symbol?.value ?? "",
                        priceUpdate: formatDate(timestampToDate(val.priceupdate?.seconds, val.priceupdate?.nanos)),
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                        isPriceLimitPercentage: val.ordersettings?.ispricelimitpercentage?.value ?? false,
                        isMatchingEngineEnabled: val.ordersettings?.ismatchingengineenabled?.value ?? false,
                        canClientCancelOrders: val.ordersettings?.canclientcancelorders?.value ?? false,
                        canPlaceOrders: val.ordersettings?.canplaceorders?.value ?? false,

                        priceTickSize: val.ordersettings?.priceticksize?.value,
                        quantityTickSize: val.ordersettings?.quantityticksize?.value,
                        priceMinLimit: val.ordersettings?.priceminlimit?.value,
                        priceMaxLimit: val.ordersettings?.pricemaxlimit?.value,
                        quantityMaxLimit: val.ordersettings?.quantitymaxlimit?.value,
                        quantityMinLimit: val.ordersettings?.quantityminlimit?.value,
                        infoURL: val?.infourl?.value,
                        openingDay: val?.ordersettings?.openingday?.value,
                        tradingDays: [] as any,
                        tradingHours: [] as any,
                        isMarketOpen: val.ismarketopen?.value,


                    }
                    if (val.ordersettings?.tradingdaysList) {
                        val.ordersettings?.tradingdaysList.forEach(e => {
                            rc.tradingDays.push(e.value);
                        })
                    }
                    if (val.ordersettings?.tradinghoursList) {
                        val.ordersettings?.tradinghoursList.forEach(e => {
                            rc.tradingHours.push(e.value);
                        })
                    }

                    if (rc.name == undefined) {
                        rc.name = (rc?.baseCurrencyName == "" ? rc?.baseCurrencyId : rc?.baseCurrencyName + " (" + rc?.baseCurrencyCode + ")") + " / " + (rc?.quoteCurrencyName == "" ? rc?.quoteCurrencyId : rc?.quoteCurrencyName + " (" + rc?.quoteCurrencyCode + ")")
                    }
                    return rc;
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                if (r.length < state.currencyPairs.numberOfResults) {
                    state.currencyPairs.hasMore = false;
                }
                state.currencyPairs.items = state.currencyPairs.items.concat(r);
                //console.log(state.items)

            } else {
                state.currencyPairs.hasMore = false;
            }
            state.currencyPairs.isFetching = false

        })
        builder.addCase(getCurrencyPairs.rejected, (state, action) => {
            state.currencyPairs.isFetching = false;
            if (action.payload) {
                //state.message = action.payload;
            }
        })
        builder.addCase(getCurrencyPairs.pending, (state, action) => {
            state.currencyPairs.isFetching = true;
            // state.message = undefined;
        })


        builder.addCase(getItems.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.ordersList.map(val => {
                    let rc = {
                        id: val.id,
                        currencyId: val.currency?.id?.value ?? "",
                        currencyName: val.currency?.name?.value ?? "",
                        currencyCode: val.currency?.code?.value ?? "",
                        currencyDecimalPlaces: val.currency?.decimalplaces?.value ?? undefined,
                        currencyForm: val.currency?.form?.value ?? undefined,
                        currencySymbol: val.currency?.symbol?.value ?? "",
                        orderType: val.ordertype?.value,
                        itemId: val.orderitem?.id?.value ?? undefined,
                        currencyPairId: val.orderitem?.item?.id?.value,
                        currencyPairName: val.orderitem?.item?.name?.value,
                        currencyPairBaseCurrencyId: val.orderitem?.item?.base?.id?.value ?? "",
                        currencyPairBaseCurrencyName: val.orderitem?.item?.base?.name?.value ?? "",
                        currencyPairBaseCurrencyCode: val.orderitem?.item?.base?.code?.value ?? "",
                        currencyPairBaseCurrencyDecimalPlaces: val.orderitem?.item?.base?.decimalplaces?.value ?? undefined,
                        currencyPairBaseCurrencyForm: val.orderitem?.item?.base?.form?.value ?? undefined,
                        currencyPairBaseCurrencySymbol: val.orderitem?.item?.base?.symbol?.value ?? "",
                        currencyPairQuoteCurrencyId: val.orderitem?.item?.quote?.id?.value ?? "",
                        currencyPairQuoteCurrencyName: val.orderitem?.item?.quote?.name?.value ?? "",
                        currencyPairQuoteCurrencyCode: val.orderitem?.item?.quote?.code?.value ?? "",
                        currencyPairQuoteCurrencyDecimalPlaces: val.orderitem?.item?.quote?.decimalplaces?.value ?? undefined,
                        currencyPairQuoteCurrencyForm: val.orderitem?.item?.quote?.form?.value ?? undefined,
                        currencyPairQuoteCurrencySymbol: val.orderitem?.item?.quote?.symbol?.value ?? "",
                        quantityFilled: val.quantityfilled?.value,
                        remainingFilled: val.remainingfilled?.value,
                        remainingQuantityFilled: val.remainingquantityfilled?.value,
                        filled: val.filled?.value ?? undefined,
                        reference: val.reference?.value,
                        itemPrice: val.orderitem?.price?.value,
                        itemQuantity: val.orderitem?.quantity?.value,
                        itemSubTotal: val.orderitem?.subtotal?.value,
                        clientId: val.client?.id?.value,
                        clientName: (val.client?.firstname?.value ?? "") + " " + (val.client?.lastname?.value ?? ""),
                        status: val.status?.value,
                        total: val.total?.value,
                        tradeAction: val.tradeaction?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                    }
                    if (rc.currencyPairName == undefined) {
                        rc.currencyPairName = (rc?.currencyPairBaseCurrencyName == "" ? rc?.currencyPairBaseCurrencyId : rc?.currencyPairBaseCurrencyName + " (" + rc?.currencyPairBaseCurrencyCode + ")") + " / " + (rc?.currencyPairQuoteCurrencyName == "" ? rc?.currencyPairQuoteCurrencyId : rc?.currencyPairQuoteCurrencyName + " (" + rc?.currencyPairQuoteCurrencyCode + ")")
                    }
                    return rc;

                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.set.items.length != 0) {
                    l = state.set.items.slice(0, state.set.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.set.numberOfResults) {
                    state.set.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.set.items = l
                //console.log(state.items)

                /*{
                        id: val.id,
                        fee: val.fee?.value,
                        fromAccountId: val.fromaccount?.id?.value ?? "",
                        fromAccountName: val.fromaccount?.name?.value ?? "",
                        currencyId: val.currency?.id?.value ?? "",
                        currencyName: val.currency?.name?.value ?? "",
                        currencyCode: val.currency?.code?.value ?? "",
                        currencyDecimalPlaces: val.currency?.decimalplaces?.value ?? undefined,
                        currencyForm: val.currency?.form?.value ?? undefined,
                        currencySymbol: val.currency?.symbol?.value ?? "",
                        netTotal: val.nettotal?.value,
                        orderType: val.ordertype?.value,
                        paid: val.paid?.value ?? undefined,
                        itemId: val.item?.id?.value ?? undefined,
                        currencyPairId: val.item?.item?.id?.value,
                        currencyPairBaseCurrencyId: val.item?.item?.base?.id?.value ?? "",
                        currencyPairBaseCurrencyName: val.item?.item?.base?.name?.value ?? "",
                        currencyPairBaseCurrencyCode: val.item?.item?.base?.code?.value ?? "",
                        currencyPairBaseCurrencyDecimalPlaces: val.item?.item?.base?.decimalplaces?.value ?? undefined,
                        currencyPairBaseCurrencyForm: val.item?.item?.base?.form?.value ?? undefined,
                        currencyPairBaseCurrencySymbol: val.item?.item?.base?.symbol?.value ?? "",
                        currencyPairQuoteCurrencyId: val.item?.item?.quote?.id?.value ?? "",
                        currencyPairQuoteCurrencyName: val.item?.item?.quote?.name?.value ?? "",
                        currencyPairQuoteCurrencyCode: val.item?.item?.quote?.code?.value ?? "",
                        currencyPairQuoteCurrencyDecimalPlaces: val.item?.item?.quote?.decimalplaces?.value ?? undefined,
                        currencyPairQuoteCurrencyForm: val.item?.item?.quote?.form?.value ?? undefined,
                        currencyPairQuoteCurrencySymbol: val.item?.item?.quote?.symbol?.value ?? "",
                        itemPrice: val.item?.price?.value,
                        itemQuantity: val.item?.quantity?.value,
                        itemSubTotal: val.item?.subtotal?.value,
                        placedforId: val.placedfor?.id?.value,
                        placedforName: (val.placedfor?.firstname?.value ?? "") + " " + (val.placedfor?.lastname?.value ?? ""),
                        reference: val.reference?.value,
                        status: val.status?.value,
                        toAccountId: val.toaccount?.id?.value ?? "",
                        toAccountName: val.toaccount?.name?.value ?? "",
                        total: val.total?.value,
                        tradeAction: val.tradeaction?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                    }*/

            } else {
                state.set.hasMore = false;
            }
            state.set.isFetching = false

        })
        builder.addCase(getItems.rejected, (state, action) => {
            state.set.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getItems.pending, (state, action) => {
            state.set.isFetching = true;
            state.message = undefined;
        })


        builder.addCase(cancelOrder.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response?.success?.message);
                let selected = state.set.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        selected.at(0).status = OrderStatus.CANCELED
                    }
                    var u = state.set.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.set.items.at(u).status = OrderStatus.CANCELED
                    }

                }
            }
        })
        builder.addCase(cancelOrder.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(cancelOrder.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })

    }
})

export const { reset, dismissMessage, setSort, resetCurrencyPairs, addOrderBookItem, resetOrderBook, resetSet, setIsFilteredCurrencyPairsSet, setIsFilteredOrderBookSet, setIsFilteredSet, setNumberOfResults, setSearchText, setSelectedCurrencyPairItems, setSelectedItems, setSelectedOrderBookItems, updateOrderBookItem } = tradingLayoutSlice.actions

export default tradingLayoutSlice.reducer


